import { AxiosResponse } from 'axios'
import get from 'lodash/get'
import { IFilter, UpdateBody, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { api } from 'API'
import { AggregationPaginated } from 'interfaces/common'
import { IGroupProcess, IProcess, IProcessWithRelations } from 'interfaces/process'
import { IStep } from 'interfaces/step'
import { IProcessDuplicate } from 'components/Pages/ProcessDetailPage/components/CopyProcessModal'

export async function getProcessById(
  processId: number,
  filter: IFilter<IProcess> = {}
): Promise<IProcessWithRelations> {
  const response: AxiosResponse = await api.get(`/processes/${processId}?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function aggregateProcesses(pipeline: AggregationPipeline = []): Promise<IProcessWithRelations[]> {
  const response: AxiosResponse = await api.post(`/processes/aggregate`, {
    pipeline
  })
  return response.data
}

export async function aggregateProcessesSearch(
  pipeline: AggregationPipeline = [],
  timeTracking: number = new Date().getTime()
): Promise<AggregationPaginated<IProcess>> {
  try {
    const response: AxiosResponse = await api.post(`/processes/aggregate/search`, {
      pipeline
    })
    response.data.timeTracking = timeTracking
    return response.data
  } catch (error: any) {
    return { paginatedResults: [], totalResults: 0, timeTracking }
  }
}

export async function aggregateCountProcesses(pipeline: AggregationPipeline = []): Promise<number> {
  const response: AxiosResponse = await api.post(`/processes/aggregate/count`, {
    pipeline
  })
  return response?.data[0]?.totalResults ?? 0
}

export async function countProcesses(where: Where<IProcess> = {}): Promise<number> {
  const response: AxiosResponse = await api.get(`/processes/count?where=${JSON.stringify(where)}`)
  return get(response, 'data.count', 0)
}

export async function searchProcesses(where: Where<IProcess & { searchText: string }> = {}): Promise<IProcess[]> {
  const response: AxiosResponse = await api.get(`/processes/search?where=${JSON.stringify(where)}`)
  return get(response, 'data')
}

export async function updateProcessById(id: number, updateBody: UpdateBody<IProcess>): Promise<void> {
  await api.patch(`/processes/${id}`, updateBody)
}

export async function getDetailProcessById(id: number): Promise<IProcessWithRelations> {
  const response: AxiosResponse = await api.get(`/processes/${id}/detail`)
  return get(response, 'data')
}

export async function createProcess(process: UpdateBody<IProcess>): Promise<IProcess> {
  const resp = await api.post('/processes', process)
  return resp.data
}

export async function getProcessStepListAPI(filter: IFilter<IStep> = {}): Promise<IStep[]> {
  const response = await api.get(`steps?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function aggregateCountProcessSteps(where: Where<IStep> = {}): Promise<number> {
  const response = await api.get(`steps/count?where=${JSON.stringify(where)}`)
  return response?.data?.count ?? 0
}

export async function getAllProcesses(filter: IFilter<IProcess> = {}): Promise<IProcessWithRelations[]> {
  const response: AxiosResponse = await api.get(`/processes?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function deleteProcessById(id: number): Promise<void> {
  await api.delete(`/processes/${id}`)
}

export async function archiveProcessById(id: number): Promise<void> {
  await api.patch(`/processes/${id}/archive`)
}

export async function restoreProcessById(id: number): Promise<void> {
  await api.patch(`/processes/${id}/restore`)
}

export async function getProcessesByOrganization(
  limit?: number,
  search?: string
): Promise<IProcessWithRelations[]> {
  const params: any = {}
  if (limit) params.limit = limit
  if (search) params.search = search

  const response: AxiosResponse = await api.get('/processes/organization', { params })
  return response.data
}

export async function restoreProcessToDraftById(id: number): Promise<void> {
  await api.patch(`/processes/${id}/restore-to-draft`)
}

export async function upsertProcessTags(id: number, tagIds: number[]): Promise<void> {
  await api.post(`/processes/${id}/tags`, { tagIds })
}

export async function duplicateProcess(processId: number, payload: IProcessDuplicate): Promise<IProcess> {
  const res: AxiosResponse = await api.post(`/processes/${processId}/duplicate`, payload)
  return res.data
}

// Edit-lock APIs for Process (to be implemented on backend)
export async function lockProcess(processId: number): Promise<void> {
  await api.post(`/processes/${processId}/lock`)
}

export async function extendLockProcess(processId: number): Promise<void> {
  await api.post(`/processes/${processId}/lock/extend`)
}

export async function releaseLockProcess(processId: number): Promise<void> {
  await api.post(`/processes/${processId}/lock/release`)
}

export async function forceLockProcess(processId: number): Promise<void> {
  await api.post(`/processes/${processId}/lock/force`)
}